import React, { useCallback, useEffect, useRef, useState } from 'react'
import adminApi from './api'
import { API_URL } from '../api/base'
import { Notification } from './components/Notification'

interface CertConfig {
  useNewDesign: boolean
  logosTop: string[]
  logosBottom: string[]
  handoutImages: string[]
  sendHandouts: boolean
}

type Position = 'top' | 'bottom'
type Language = 'ru' | 'ky' | 'en'
type Tab = 'certificate' | 'handouts' | 'thankyou'

const MAX_TOP = 8
const MAX_BOTTOM = 30

// Референс из SVG-макета A4 - 7.svg (842 × 595 px)
const CANVAS_W = 842
const CANVAS_H = 595

// === Точные значения из Figma (fileKey uwwAhApsAfs7u69IAINrZo, node 1494:2) ===
// Верхний ряд: слоты 48×24 px, gap 10px, border-radius 8px
const TOP_SLOT_W = 48
const TOP_SLOT_H = 24
const TOP_SLOT_Y = 36
const TOP_GAP = 10
// Логотип бренда: 67×67 (SVG уже содержит подпись «Жашыл дем» внутри)
const BRAND_W = 67
const BRAND_H = 67
const BRAND_X = 421 - BRAND_W / 2 // = 387.5
const BRAND_Y = 16
// Отступ от логотипа до ближайшего слота
const BRAND_GAP = 20
const TOP_LEFT_END_X = BRAND_X - BRAND_GAP // 371
const TOP_LEFT_XS = [
  TOP_LEFT_END_X - 4 * TOP_SLOT_W - 3 * TOP_GAP,          // slot 0 (крайний слева)
  TOP_LEFT_END_X - 3 * TOP_SLOT_W - 2 * TOP_GAP,          // slot 1
  TOP_LEFT_END_X - 2 * TOP_SLOT_W - 1 * TOP_GAP,          // slot 2
  TOP_LEFT_END_X - 1 * TOP_SLOT_W                          // slot 3 (ближе к центру)
]
const TOP_RIGHT_START_X = BRAND_X + BRAND_W + BRAND_GAP // 471
const TOP_RIGHT_XS = [
  TOP_RIGHT_START_X + 0 * (TOP_SLOT_W + TOP_GAP),
  TOP_RIGHT_START_X + 1 * (TOP_SLOT_W + TOP_GAP),
  TOP_RIGHT_START_X + 2 * (TOP_SLOT_W + TOP_GAP),
  TOP_RIGHT_START_X + 3 * (TOP_SLOT_W + TOP_GAP)
]

// Info card: width=474, padding=16, radius=25, bg rgba(0,0,0,0.5), backdrop-blur 4px
// Позиция: центр, top ~215 (после подзаголовка). Из Figma: left=218, top=184, но с учётом контента.
const CARD_W = 474
const CARD_H = 138
const CARD_X = (842 - CARD_W) / 2 // 184
const CARD_Y = 219  // 195 (конец subtitle) + 24 gap

// Заголовок «Сертификат»: 40px Bold; подзаголовок 28px Medium.
// Отступ между заголовком и подзаголовком = 10px
// Отступ между подзаголовком и info-card = 24px
const TITLE_Y = 100  // высота ~50 → кончается на y=150
const SUBTITLE_Y = 160  // 150 + 10 gap; высота ~35 → кончается на y=195

// Директор + подпись + печать: правый нижний угол
// Из Figma: директор блок left=173 top=403 (rotated); подпись 139×78; печать 131×131 повёрнута 15°
// В user coords для landscape: подпись справа снизу, печать поверх с наклоном 15°
const SIGNATURE_W = 139
const SIGNATURE_H = 78
const STAMP_SIZE = 130

// Нижняя сетка 10×3: слоты 48×24, gap 10px, radius 8px
const BOTTOM_SLOT_W = 48
const BOTTOM_SLOT_H = 24
const BOTTOM_GAP_X = 10
const BOTTOM_TOTAL_W = 10 * BOTTOM_SLOT_W + 9 * BOTTOM_GAP_X // 610
const BOTTOM_LEFT_X = (842 - BOTTOM_TOTAL_W) / 2 // 116
// 3 ряда: gap 10 → шаг 34 (высота 24 + gap 10)
// Плашка CO2 ~24px внизу. Нижний ряд заканчивается ~y=554 → отступ до плашки ~10px
// Верхний y=462, средний y=496, нижний y=530
const BOTTOM_YS = [462, 496, 530]
const BOTTOM_XS = Array.from({ length: 10 }, (_, i) => BOTTOM_LEFT_X + i * (BOTTOM_SLOT_W + BOTTOM_GAP_X))

const pctX = (x: number) => `${(x / CANVAS_W) * 100}%`
const pctY = (y: number) => `${(y / CANVAS_H) * 100}%`
const pctW = (w: number) => `${(w / CANVAS_W) * 100}%`
const pctH = (h: number) => `${(h / CANVAS_H) * 100}%`

const absoluteUrl = (relOrAbs: string) => {
  if (!relOrAbs) return ''
  if (relOrAbs.startsWith('http')) return relOrAbs
  return `${API_URL}${relOrAbs.startsWith('/') ? '' : '/'}${relOrAbs}`
}

const strings: Record<Language, { title: string; subtitle: string; desc: string; director: string; directorName: string; name: string; certLabel: string; co2: string }> = {
  ru: {
    title: 'Сертификат',
    subtitle: 'на посадку 10 деревьев',
    desc: 'настоящим подтверждается как участник экологической инициативы «Жашыл Дем» и внёс свой вклад в озеленение нашей природы, поддержав посадку дерева.',
    director: 'Директор ОсОО «Жашыл Дем»',
    directorName: 'Исражидин Зарипов',
    name: 'Саматов Айбек',
    certLabel: 'Номер',
    co2: '* Посадка 1 дерева компенсирует 0.083 тонн CO2'
  },
  ky: {
    title: 'Күбөлүк',
    subtitle: '10 дарак отургузууга',
    desc: 'ушул күбөлүк аркылуу «Жашыл Дем» экологиялык демилгесинин катышуучусу катары табиятыбызды көгөртүүгө өз салымын кошкондугу тастыкталат.',
    director: '«Жашыл Дем» ЖЧКнын директору',
    directorName: 'Исражидин Зарипов',
    name: 'Саматов Айбек',
    certLabel: 'Номер',
    co2: '* 1 даракты отургузуу 0.083 тонна CO2 компенсациялайт'
  },
  en: {
    title: 'Certificate',
    subtitle: 'for planting 10 trees',
    desc: 'is hereby confirmed as a participant of the «Jashyl Dem» environmental initiative and has contributed to greening our nature by supporting tree planting.',
    director: 'Director of «Jashyl Dem» LLC',
    directorName: 'Israzhidin Zaripov',
    name: 'Samatov Aibek',
    certLabel: 'Number',
    co2: '* Planting 1 tree offsets 0.083 tons of CO2'
  }
}

export const LettersCertificatesAdmin: React.FC = () => {
  const isReadonly = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('readonly') === '1'
  const [config, setConfig] = useState<CertConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [savingFlag, setSavingFlag] = useState(false)
  const [uploading, setUploading] = useState<string | null>(null)
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [previewLanguage, setPreviewLanguage] = useState<Language>(() => {
    if (typeof window === 'undefined') return 'ru'
    const l = new URLSearchParams(window.location.search).get('lang')
    return (l === 'ky' || l === 'en') ? l : 'ru'
  })
  const [activeTab, setActiveTab] = useState<Tab>('certificate')
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [thankYouModalOpen, setThankYouModalOpen] = useState(false)
  const [handoutsModalOpen, setHandoutsModalOpen] = useState(false)
  const [previewToken, setPreviewToken] = useState(0)
  const [uploadingHandout, setUploadingHandout] = useState(false)
  const [assets, setAssets] = useState<{ bg: string; brand: string; signature: string; stamp: string } | null>(null)

  const [draggedIndex, setDraggedIndex] = useState<{ position: Position; index: number } | null>(null)
  const [draggedHandoutIndex, setDraggedHandoutIndex] = useState<number | null>(null)
  const handoutFileRef = useRef<HTMLInputElement | null>(null)

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await adminApi.get<CertConfig>('/api/admin/certificate-config')
      setConfig(data)
    } catch {
      setNotification({ message: 'Не удалось загрузить настройки', type: 'error' })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  useEffect(() => {
    setAssets({
      bg: `${API_URL}/email-assets/cert-bg-new.jpg`,
      brand: `${API_URL}/email-assets/logo-full.svg`,
      signature: `${API_URL}/email-assets/signature.svg`,
      stamp: `${API_URL}/email-assets/stamp.svg`
    })
  }, [])

  const setUseNewDesign = async (val: boolean) => {
    setSavingFlag(true)
    try {
      const { data } = await adminApi.put<CertConfig>('/api/admin/certificate-config/design', {
        useNewDesign: val
      })
      setConfig(data)
      setNotification({ message: 'Настройки сохранены', type: 'success' })
    } catch {
      setNotification({ message: 'Не удалось сохранить', type: 'error' })
    } finally {
      setSavingFlag(false)
    }
  }

  const setSendHandouts = async (val: boolean) => {
    setSavingFlag(true)
    try {
      const { data } = await adminApi.put<CertConfig>('/api/admin/certificate-config/send-handouts', {
        sendHandouts: val
      })
      setConfig(data)
      setNotification({ message: 'Настройки сохранены', type: 'success' })
    } catch {
      setNotification({ message: 'Не удалось сохранить', type: 'error' })
    } finally {
      setSavingFlag(false)
    }
  }

  const uploadLogo = async (position: Position, file: File, opKey: string, slotIndex?: number) => {
    if (position === 'top' && config && config.logosTop.length >= MAX_TOP) {
      setNotification({ message: `Максимум ${MAX_TOP} логотипов в верхнем ряду`, type: 'error' })
      return
    }
    if (position === 'bottom' && config && config.logosBottom.length >= MAX_BOTTOM) {
      setNotification({ message: `Максимум ${MAX_BOTTOM} логотипов в нижней сетке`, type: 'error' })
      return
    }
    setUploading(opKey)
    try {
      const form = new FormData()
      form.append('logo', file)
      if (typeof slotIndex === 'number') form.append('slotIndex', String(slotIndex))
      const { data } = await adminApi.post<CertConfig & { url: string }>(
        `/api/admin/certificate-config/logos/${position}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      setConfig({ useNewDesign: data.useNewDesign, logosTop: data.logosTop, logosBottom: data.logosBottom, handoutImages: data.handoutImages || [], sendHandouts: !!data.sendHandouts })
      setPreviewToken((t) => t + 1)
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Ошибка загрузки'
      setNotification({ message: msg, type: 'error' })
    } finally {
      setUploading(null)
    }
  }

  const deleteLogo = async (position: Position, index: number) => {
    try {
      const { data } = await adminApi.delete<CertConfig>(
        `/api/admin/certificate-config/logos/${position}/${index}`
      )
      setConfig(data)
      setPreviewToken((t) => t + 1)
    } catch {
      setNotification({ message: 'Не удалось удалить', type: 'error' })
    }
  }

  const reorderLogos = async (position: Position, newUrls: string[]) => {
    if (config) {
      setConfig({
        ...config,
        [position === 'top' ? 'logosTop' : 'logosBottom']: newUrls
      })
    }
    try {
      const { data } = await adminApi.put<CertConfig>(
        `/api/admin/certificate-config/logos/${position}/reorder`,
        { urls: newUrls }
      )
      setConfig(data)
      setPreviewToken((t) => t + 1)
    } catch {
      setNotification({ message: 'Не удалось изменить порядок', type: 'error' })
      fetchConfig()
    }
  }

  // === Раздаточные материалы (handouts) ===
  const uploadHandout = async (file: File) => {
    setUploadingHandout(true)
    try {
      const form = new FormData()
      form.append('image', file)
      const { data } = await adminApi.post<CertConfig & { url: string }>(
        `/api/admin/certificate-config/handouts`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      setConfig({
        useNewDesign: data.useNewDesign,
        logosTop: data.logosTop,
        logosBottom: data.logosBottom,
        handoutImages: data.handoutImages || [],
        sendHandouts: !!data.sendHandouts
      })
      setPreviewToken((t) => t + 1)
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Ошибка загрузки'
      setNotification({ message: msg, type: 'error' })
    } finally {
      setUploadingHandout(false)
    }
  }

  const deleteHandout = async (index: number) => {
    try {
      const { data } = await adminApi.delete<CertConfig>(`/api/admin/certificate-config/handouts/${index}`)
      setConfig(data)
      setPreviewToken((t) => t + 1)
    } catch {
      setNotification({ message: 'Не удалось удалить', type: 'error' })
    }
  }

  const reorderHandouts = async (newUrls: string[]) => {
    if (config) setConfig({ ...config, handoutImages: newUrls })
    try {
      const { data } = await adminApi.put<CertConfig>(`/api/admin/certificate-config/handouts/reorder`, { urls: newUrls })
      setConfig(data)
      setPreviewToken((t) => t + 1)
    } catch {
      setNotification({ message: 'Не удалось изменить порядок', type: 'error' })
      fetchConfig()
    }
  }

  const onHandoutDragStart = (index: number) => setDraggedHandoutIndex(index)
  const onHandoutDrop = (dropIndex: number) => {
    if (draggedHandoutIndex === null || !config) {
      setDraggedHandoutIndex(null)
      return
    }
    if (draggedHandoutIndex === dropIndex) {
      setDraggedHandoutIndex(null)
      return
    }
    const urls = [...config.handoutImages]
    const [moved] = urls.splice(draggedHandoutIndex, 1)
    urls.splice(dropIndex, 0, moved)
    reorderHandouts(urls)
    setDraggedHandoutIndex(null)
  }

  const handleHandoutFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) uploadHandout(f)
    e.target.value = ''
  }

  const onDragStart = (position: Position, index: number) => setDraggedIndex({ position, index })
  const onDragOver = (e: React.DragEvent) => e.preventDefault()
  const onDrop = (position: Position, dropIndex: number) => {
    if (!draggedIndex || draggedIndex.position !== position || !config) {
      setDraggedIndex(null)
      return
    }
    if (draggedIndex.index === dropIndex) {
      setDraggedIndex(null)
      return
    }
    const source = position === 'top' ? [...config.logosTop] : [...config.logosBottom]
    if (draggedIndex.index >= source.length) {
      setDraggedIndex(null)
      return
    }
    const [moved] = source.splice(draggedIndex.index, 1)
    const targetIndex = Math.min(dropIndex, source.length)
    source.splice(targetIndex, 0, moved)
    reorderLogos(position, source)
    setDraggedIndex(null)
  }

  if (loading) return <div className="p-6">Загрузка…</div>
  if (!config) return null

  // Readonly-режим: только сам канвас во всё окно (для iframe в модалке)
  if (isReadonly) {
    return (
      <div style={{ padding: 0, margin: 0, background: 'transparent', width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{`
          html, body, #root { margin: 0; padding: 0; background: transparent; overflow: hidden; }
          body { background: transparent !important; }
          @font-face { font-family: 'MontserratLocal'; src: url(${API_URL}/email-assets/Montserrat-Regular.ttf) format('truetype'); font-weight: 400; }
          @font-face { font-family: 'MontserratLocal'; src: url(${API_URL}/email-assets/Montserrat-Medium.ttf) format('truetype'); font-weight: 500; }
          @font-face { font-family: 'MontserratAlternatesLocal'; src: url(${API_URL}/email-assets/MontserratAlternates-SemiBold.ttf) format('truetype'); font-weight: 600; }
          [data-testid="cert-canvas"] { width: min(100vw, calc(100vh * ${CANVAS_W} / ${CANVAS_H})) !important; height: auto !important; }
        `}</style>
        <ReadOnlyCanvas config={config} language={previewLanguage} />
      </div>
    )
  }

  const s = strings[previewLanguage]

  // Верхний ряд: отображение слева направо.
  // Слоты 0..3 → topLeft, но в порядке дизайна: config.logosTop[0] = крайний слева (первый добавленный).
  // TOP_LEFT_XS[0]=133.5 (крайний слева), TOP_LEFT_XS[3]=325.5 (ближе к центру).
  // Значит слот index=0 занимает TOP_LEFT_XS[0], index=1 → TOP_LEFT_XS[1] и т.д.
  // Заполнение от центра к краям:
  // Левый ряд: TOP_LEFT_XS[3] (ближе к центру) = index 0, TOP_LEFT_XS[0] (крайний слева) = index 3
  // Правый ряд: TOP_RIGHT_XS[0] (ближе к центру) = index 4, TOP_RIGHT_XS[3] (крайний справа) = index 7
  const topLeftSlots = TOP_LEFT_XS.map((x, i) => ({ x, index: 3 - i }))
  const topRightSlots = TOP_RIGHT_XS.map((x, i) => ({ x, index: 4 + i }))

  // Нижняя сетка 10×3. Заполнение снизу-вверх, слева-направо:
  // index 0..9 → нижний ряд, 10..19 → средний, 20..29 → верхний.
  // BOTTOM_YS[0]=463.5 (верхний ряд в DOM), [1]=509.5 (средний), [2]=555.5 (нижний)
  const bottomSlots: { x: number; y: number; index: number }[] = []
  for (let i = 0; i < 30; i++) {
    const rowFromBottom = Math.floor(i / 10) // 0 = нижний, 1 = средний, 2 = верхний
    const posInRow = i % 10
    const yIndex = 2 - rowFromBottom // 0 = верхний
    bottomSlots.push({ x: BOTTOM_XS[posInRow], y: BOTTOM_YS[yIndex], index: i })
  }

  const showCo2Plate = config.logosBottom.length === 0

  return (
    <div>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="page-header">
        <h1 className="page-title">Письма и сертификаты</h1>
        <p className="page-subtitle">Настройка макета сертификата, благодарственного письма и раздаточных материалов</p>
      </div>

      {/* Табы */}
      <div className="cert-tabs">
        <button
          type="button"
          className={'cert-tab ' + (activeTab === 'certificate' ? 'active' : '')}
          onClick={() => setActiveTab('certificate')}
        >
          Сертификат
        </button>
        <button
          type="button"
          className={'cert-tab ' + (activeTab === 'handouts' ? 'active' : '')}
          onClick={() => setActiveTab('handouts')}
        >
          Раздаточные материалы
        </button>
        <button
          type="button"
          className={'cert-tab ' + (activeTab === 'thankyou' ? 'active' : '')}
          onClick={() => setActiveTab('thankyou')}
        >
          Благодарственное письмо
        </button>
      </div>

      {/* ===== Таб: Сертификат ===== */}
      {activeTab === 'certificate' && (
      <>
      <div className="cert-card mb-4">
        <label className="cert-toggle-row">
          <input
            type="checkbox"
            checked={config.useNewDesign}
            disabled={savingFlag}
            onChange={(e) => setUseNewDesign(e.target.checked)}
          />
          <div>
            <div className="cert-toggle-title">Отправлять клиентам новый дизайн сертификата</div>
            <div className="cert-toggle-desc">
              Если выключено — клиентам отправляется прежний дизайн. Настройки макета ниже сохраняются в любом случае.
            </div>
          </div>
        </label>
      </div>

      <div className="cert-hint-row mb-3">
        <div className="cert-lang-switch">
          {(['ru', 'ky', 'en'] as Language[]).map((lng) => (
            <button
              key={lng}
              type="button"
              onClick={() => setPreviewLanguage(lng)}
              className={'cert-lang-btn ' + (previewLanguage === lng ? 'active' : '')}
            >
              {lng.toUpperCase()}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => { setPreviewToken((t) => t + 1); setPreviewModalOpen(true) }}
        >
          Предпросмотр
        </button>
      </div>

      {/* Редактор */}
      <div className="cert-card cert-editor-wrap">
        {/* Инжектим Montserrat + MontserratAlternates */}
        <style>{`
          @font-face {
            font-family: 'MontserratLocal';
            src: url(${API_URL}/email-assets/Montserrat-Regular.ttf) format('truetype');
            font-weight: 400;
            font-display: swap;
          }
          @font-face {
            font-family: 'MontserratLocal';
            src: url(${API_URL}/email-assets/Montserrat-Medium.ttf) format('truetype');
            font-weight: 500;
            font-display: swap;
          }
          @font-face {
            font-family: 'MontserratAlternatesLocal';
            src: url(${API_URL}/email-assets/MontserratAlternates-SemiBold.ttf) format('truetype');
            font-weight: 600;
            font-display: swap;
          }
        `}</style>
        <div
          data-testid="cert-canvas"
          className="cert-canvas relative mx-auto overflow-hidden rounded-md text-white"
          style={{
            width: '70%',
            aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
            backgroundImage: assets ? `url(${assets.bg})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: '#0a3a68',
            fontFamily: 'MontserratLocal, Montserrat, system-ui, sans-serif',
            containerType: 'size'
          }}
        >
          {/* Общее затемнение фона black 0.2 (как в макете) — под всем контентом.
              Плюс фильтр яркости на фоновой картинке симулирует «Rectangle с blur+black0.2» из Figma-макета. */}
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'rgba(0,0,0,0.35)', zIndex: 0 }} />
          {/* Контент располагается поверх overlay */}
          <div className="absolute inset-0" style={{ zIndex: 1 }}>
          {/* Центральный логотип Жашыл дем (SVG уже содержит подпись) */}
          <div
            className="absolute flex items-center justify-center"
            style={{
              left: pctX(BRAND_X),
              top: pctY(BRAND_Y),
              width: pctW(BRAND_W),
              height: pctH(BRAND_H)
            }}
          >
            {assets && (
              <img
                src={assets.brand}
                alt="Жашыл дем"
                className="max-h-full max-w-full object-contain"
                draggable={false}
              />
            )}
          </div>

          {/* Верхние слоты — левая часть. При клике на пустой слот лого попадает в первый свободный индекс 0..3 (ближайший к центру). */}
          {topLeftSlots.map(({ x, index }) => {
            const firstFreeLeft = [0, 1, 2, 3].find((i) => !config.logosTop[i])
            return (
              <LogoSlot
                key={`top-${index}`}
                position="top"
                index={index}
                url={config.logosTop[index]}
                uploading={uploading === `top-${index}`}
                onUpload={(file) => uploadLogo('top', file, `top-${index}`, firstFreeLeft)}
                onDelete={() => deleteLogo('top', index)}
                onDragStart={() => onDragStart('top', index)}
                onDragOver={onDragOver}
                onDrop={() => onDrop('top', index)}
                style={{
                  left: pctX(x),
                  top: pctY(TOP_SLOT_Y),
                  width: pctW(TOP_SLOT_W),
                  height: pctH(TOP_SLOT_H)
                }}
              />
            )
          })}

          {/* Верхние слоты — правая часть. При клике лого попадает в первый свободный индекс 4..7. */}
          {topRightSlots.map(({ x, index }) => {
            const firstFreeRight = [4, 5, 6, 7].find((i) => !config.logosTop[i])
            return (
              <LogoSlot
                key={`top-${index}`}
                position="top"
                index={index}
                url={config.logosTop[index]}
                uploading={uploading === `top-${index}`}
                onUpload={(file) => uploadLogo('top', file, `top-${index}`, firstFreeRight)}
                onDelete={() => deleteLogo('top', index)}
                onDragStart={() => onDragStart('top', index)}
                onDragOver={onDragOver}
                onDrop={() => onDrop('top', index)}
                style={{
                  left: pctX(x),
                  top: pctY(TOP_SLOT_Y),
                  width: pctW(TOP_SLOT_W),
                  height: pctH(TOP_SLOT_H)
                }}
              />
            )
          })}

          {/* Заголовок */}
          {/* Заголовок «Сертификат» — 40px Montserrat Alternates Bold */}
          <div
            data-alt-font
            className="absolute text-center"
            style={{
              left: 0,
              right: 0,
              top: pctY(TITLE_Y),
              fontWeight: 700,
              fontSize: `${(40 / CANVAS_H) * 100}cqh`,
              textShadow: '0px 4px 4px rgba(0,0,0,0.25)'
            }}
          >
            <span data-testid="cert-title">{s.title}</span>
          </div>
          {/* Подзаголовок «на посадку 10 деревьев» — 28px Montserrat Alternates Medium */}
          <div
            data-alt-font
            className="absolute text-center"
            style={{
              left: 0,
              right: 0,
              top: pctY(SUBTITLE_Y),
              fontWeight: 500,
              fontSize: `${(28 / CANVAS_H) * 100}cqh`,
              textShadow: '0px 4px 4px rgba(0,0,0,0.25)'
            }}
          >
            {s.subtitle}
          </div>

          {/* Info card: 474×138 (autoheight), radius 25, bg rgba(0,0,0,0.5), backdrop-blur 4 */}
          <div
            className="absolute text-center"
            style={{
              left: pctX(CARD_X),
              top: pctY(CARD_Y),
              width: pctW(CARD_W),
              padding: `${(16 / CANVAS_H) * 100}cqh ${(16 / CANVAS_W) * 100}cqw`,
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              borderRadius: '25px',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              gap: `${(10 / CANVAS_H) * 100}cqh`,
              alignItems: 'center'
            }}
          >
            {/* ФИО — 20px Montserrat Alternates SemiBold */}
            <div
              data-alt-font
              style={{
                fontWeight: 600,
                fontSize: `${(20 / CANVAS_H) * 100}cqh`
              }}
            >
              {s.name}
            </div>
            {/* Описание — 15px Montserrat Regular */}
            <div style={{ fontSize: `${(15 / CANVAS_H) * 100}cqh`, lineHeight: 'normal', width: '100%' }}>
              {s.desc}
            </div>
          </div>

          {/* Директор — курсив, слева от подписи, вертикально по центру подписи+печати */}
          <div
            className="absolute text-right"
            style={{
              right: pctX(842 - 560 + 10),
              top: pctY(373),  // 357 (низ info-card) + 16 gap
              fontSize: `${(14 / CANVAS_H) * 100}cqh`,
              lineHeight: 1.35,
              fontStyle: 'italic',
              textShadow: '0px 1px 2px rgba(0,0,0,0.4)',
              whiteSpace: 'nowrap'
            }}
          >
            <div>{s.director}</div>
            <div>{s.directorName}</div>
          </div>

          {/* Подпись + печать — единый блок, выровненный по вертикальному центру.
              Печать на 36px правее подписи (по left-краям). */}
          {assets && (() => {
            const SIG_LEFT = 560
            const SIG_TOP = 365
            const SIG_CENTER_Y = SIG_TOP + SIGNATURE_H / 2 // центр подписи по Y
            const STAMP_LEFT = SIG_LEFT + 36
            const STAMP_TOP = SIG_CENTER_Y - STAMP_SIZE / 2 // выравниваем центры
            return (
              <>
                <img
                  src={assets.signature}
                  alt=""
                  draggable={false}
                  className="absolute"
                  style={{
                    left: pctX(SIG_LEFT),
                    top: pctY(SIG_TOP),
                    width: pctW(SIGNATURE_W),
                    height: pctH(SIGNATURE_H)
                  }}
                />
                <img
                  src={assets.stamp}
                  alt=""
                  draggable={false}
                  className="absolute"
                  style={{
                    left: pctX(STAMP_LEFT),
                    top: pctY(STAMP_TOP),
                    width: pctW(STAMP_SIZE),
                    height: pctH(STAMP_SIZE),
                    transform: 'rotate(15deg)',
                    transformOrigin: 'center'
                  }}
                />
              </>
            )
          })()}

          {/* Номер сертификата — правый верхний угол, отступ 16px от краёв */}
          <div
            className="absolute"
            style={{
              right: pctX(16),
              top: pctY(16),
              fontSize: `${(11 / CANVAS_H) * 100}cqh`,
              color: 'rgba(255,255,255,0.9)',
              textShadow: '0 1px 3px rgba(0,0,0,0.4)'
            }}
          >
            {s.certLabel}: JD2026000042
          </div>

          {/* Плашка CO2 — фон #053E00 с 30% прозрачности */}
          <div
            className="absolute left-0 right-0 bottom-0 text-center"
            style={{
              background: 'rgba(5, 62, 0, 0.3)',
              color: '#fff',
              padding: `${(6 / CANVAS_H) * 100}cqh ${(12 / CANVAS_W) * 100}cqw`,
              fontSize: `${(11 / CANVAS_H) * 100}cqh`,
              lineHeight: 1.3
            }}
          >
            {s.co2}
          </div>

          {/* Нижняя сетка 10×3 — слоты (всегда рендерим, но при пустом состоянии приглушены) */}
          {bottomSlots.map(({ x, y, index }) => {
            const filled = index < config.logosBottom.length
            return (
              <LogoSlot
                key={`bottom-${index}`}
                position="bottom"
                index={index}
                url={config.logosBottom[index]}
                uploading={uploading === `bottom-${index}`}
                onUpload={(file) => uploadLogo('bottom', file, `bottom-${index}`)}
                onDelete={() => deleteLogo('bottom', index)}
                onDragStart={() => onDragStart('bottom', index)}
                onDragOver={onDragOver}
                onDrop={() => onDrop('bottom', index)}
                style={{
                  left: pctX(x),
                  top: pctY(y),
                  width: pctW(BOTTOM_SLOT_W),
                  height: pctH(BOTTOM_SLOT_H),
                  opacity: filled ? 1 : 0.6
                }}
              />
            )
          })}
          </div>
        </div>
      </div>

      <div className="cert-counters">
        Верхний ряд: {config.logosTop.length}/{MAX_TOP} · Нижняя сетка: {config.logosBottom.length}/{MAX_BOTTOM}
      </div>
      </>
      )}

      {/* ===== Таб: Раздаточные материалы ===== */}
      {activeTab === 'handouts' && (
      <>
      <div className="cert-card mb-4">
        <label className="cert-toggle-row">
          <input
            type="checkbox"
            checked={config.sendHandouts}
            disabled={savingFlag}
            onChange={(e) => setSendHandouts(e.target.checked)}
          />
          <div>
            <div className="cert-toggle-title">Прикладывать раздаточные материалы к письмам</div>
            <div className="cert-toggle-desc">
              Если выключено или картинок нет — PDF раздатки не отправляется. Работает только при включённом новом дизайне сертификата.
            </div>
          </div>
        </label>
      </div>

      <div className="cert-hint-row mb-3">
        <div className="cert-hint">
          Картинки идут одна под другой в PDF. Перетаскивайте чтобы поменять порядок. Ограничений по количеству нет.
        </div>
        <div className="cert-header-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => handoutFileRef.current?.click()}
            disabled={uploadingHandout}
          >
            {uploadingHandout ? 'Загрузка…' : 'Добавить картинку'}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => { setPreviewToken((t) => t + 1); setHandoutsModalOpen(true) }}
          >
            Предпросмотр
          </button>
        </div>
        <input
          ref={handoutFileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          hidden
          onChange={handleHandoutFile}
        />
      </div>

      <div className="cert-card">
        {config.handoutImages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-text">
              Материалы не добавлены. Нажмите «Добавить картинку» чтобы загрузить первый файл.
            </div>
          </div>
        ) : (
          <div className="handout-editor-list">
            {config.handoutImages.map((url, i) => (
              <div
                key={url + i}
                className="handout-editor-item"
                draggable
                onDragStart={() => onHandoutDragStart(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onHandoutDrop(i)}
              >
                <div className="handout-editor-index">{i + 1}</div>
                <div className="handout-editor-preview">
                  <img src={url.startsWith('http') ? url : `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`} alt="" />
                </div>
                <button
                  type="button"
                  className="btn-danger btn-sm"
                  onClick={() => deleteHandout(i)}
                >
                  Удалить
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      </>
      )}

      {/* ===== Таб: Благодарственное письмо ===== */}
      {activeTab === 'thankyou' && (
      <>
      <div className="cert-hint-row mb-3">
        <div className="cert-hint"></div>
        <div className="cert-header-actions">
          <div className="cert-lang-switch">
            {(['ru', 'ky', 'en'] as Language[]).map((lng) => (
              <button
                key={lng}
                type="button"
                onClick={() => setPreviewLanguage(lng)}
                className={'cert-lang-btn ' + (previewLanguage === lng ? 'active' : '')}
              >
                {lng.toUpperCase()}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => { setPreviewToken((t) => t + 1); setThankYouModalOpen(true) }}
          >
            Предпросмотр
          </button>
        </div>
      </div>

      <div className="cert-card">
        <div className="empty-state-text">
          Нажмите «Предпросмотр» чтобы посмотреть готовое благодарственное письмо.
        </div>
      </div>
      </>
      )}

      {previewModalOpen && (
        <PreviewModal
          language={previewLanguage}
          token={previewToken}
          onClose={() => setPreviewModalOpen(false)}
        />
      )}
      {thankYouModalOpen && (
        <ThankYouModal
          language={previewLanguage}
          token={previewToken}
          onClose={() => setThankYouModalOpen(false)}
        />
      )}
      {handoutsModalOpen && (
        <HandoutsModal
          language={previewLanguage}
          token={previewToken}
          onClose={() => setHandoutsModalOpen(false)}
        />
      )}
    </div>
  )
}

/**
 * ReadOnlyCanvas — pixel-perfect копия канваса из редактора.
 * Использует те же константы позиций/размеров и ту же разметку, только без обработчиков upload/drag/delete.
 * Используется в readonly-режиме (когда LettersCertificatesAdmin открыт с ?readonly=1 внутри iframe модалки).
 */
const ReadOnlyCanvas: React.FC<{ config: CertConfig; language: Language }> = ({ config, language }) => {
  const s = strings[language]

  const topLeftSlots = TOP_LEFT_XS.map((x, i) => ({ x, index: 3 - i }))
  const topRightSlots = TOP_RIGHT_XS.map((x, i) => ({ x, index: 4 + i }))
  const bottomSlots: { x: number; y: number; index: number }[] = []
  for (let i = 0; i < 30; i++) {
    const rowFromBottom = Math.floor(i / 10)
    const posInRow = i % 10
    const yIndex = 2 - rowFromBottom
    bottomSlots.push({ x: BOTTOM_XS[posInRow], y: BOTTOM_YS[yIndex], index: i })
  }

  const assets = {
    bg: `${API_URL}/email-assets/cert-bg-new.jpg`,
    brand: `${API_URL}/email-assets/logo-full.svg`,
    signature: `${API_URL}/email-assets/signature.svg`,
    stamp: `${API_URL}/email-assets/stamp.svg`
  }

  return (
    <div
      data-testid="cert-canvas"
      className="cert-canvas relative mx-auto overflow-hidden rounded-md text-white"
      style={{
        width: '70%',
        aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
        backgroundImage: `url(${assets.bg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#0a3a68',
        fontFamily: 'MontserratLocal, Montserrat, system-ui, sans-serif',
        containerType: 'size'
      }}
    >
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'rgba(0,0,0,0.35)', zIndex: 0 }} />
      <div className="absolute inset-0" style={{ zIndex: 1 }}>
        <div className="absolute flex items-center justify-center" style={{ left: pctX(BRAND_X), top: pctY(BRAND_Y), width: pctW(BRAND_W), height: pctH(BRAND_H) }}>
          <img src={assets.brand} alt="Жашыл дем" className="max-h-full max-w-full object-contain" draggable={false} />
        </div>

        {[...topLeftSlots, ...topRightSlots].map(({ x, index }) => {
          const url = config.logosTop[index]
          return (
            <div
              key={`top-${index}`}
              className="absolute flex items-center justify-center rounded"
              style={{
                left: pctX(x),
                top: pctY(TOP_SLOT_Y),
                width: pctW(TOP_SLOT_W),
                height: pctH(TOP_SLOT_H),
                border: url ? '1px solid rgba(255,255,255,0.7)' : '1.5px dashed rgba(255,255,255,0.9)',
                background: 'rgba(0,0,0,0.35)',
                borderRadius: '8px'
              }}
            >
              {url && <img src={absoluteUrl(url)} alt="" className="max-h-full max-w-full object-contain" draggable={false} />}
            </div>
          )
        })}

        <div data-alt-font className="absolute text-center" style={{ left: 0, right: 0, top: pctY(TITLE_Y), fontWeight: 700, fontSize: `${(40 / CANVAS_H) * 100}cqh`, textShadow: '0px 4px 4px rgba(0,0,0,0.25)' }}>
          <span>{s.title}</span>
        </div>
        <div data-alt-font className="absolute text-center" style={{ left: 0, right: 0, top: pctY(SUBTITLE_Y), fontWeight: 500, fontSize: `${(28 / CANVAS_H) * 100}cqh`, textShadow: '0px 4px 4px rgba(0,0,0,0.25)' }}>
          {s.subtitle}
        </div>

        <div className="absolute text-center" style={{ left: pctX(CARD_X), top: pctY(CARD_Y), width: pctW(CARD_W), padding: `${(16 / CANVAS_H) * 100}cqh ${(16 / CANVAS_W) * 100}cqw`, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', borderRadius: '25px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: `${(10 / CANVAS_H) * 100}cqh`, alignItems: 'center' }}>
          <div data-alt-font style={{ fontWeight: 600, fontSize: `${(20 / CANVAS_H) * 100}cqh` }}>{s.name}</div>
          <div style={{ fontSize: `${(15 / CANVAS_H) * 100}cqh`, lineHeight: 'normal', width: '100%' }}>{s.desc}</div>
        </div>

        <div className="absolute text-right" style={{ right: pctX(842 - 560 + 10), top: pctY(373), fontSize: `${(14 / CANVAS_H) * 100}cqh`, lineHeight: 1.35, fontStyle: 'italic', textShadow: '0px 1px 2px rgba(0,0,0,0.4)', whiteSpace: 'nowrap' }}>
          <div>{s.director}</div>
          <div>{s.directorName}</div>
        </div>

        {(() => {
          const SIG_LEFT = 560, SIG_TOP = 365
          const SIG_CENTER_Y = SIG_TOP + SIGNATURE_H / 2
          const STAMP_LEFT = SIG_LEFT + 36
          const STAMP_TOP = SIG_CENTER_Y - STAMP_SIZE / 2
          return (
            <>
              <img src={assets.signature} alt="" draggable={false} className="absolute" style={{ left: pctX(SIG_LEFT), top: pctY(SIG_TOP), width: pctW(SIGNATURE_W), height: pctH(SIGNATURE_H) }} />
              <img src={assets.stamp} alt="" draggable={false} className="absolute" style={{ left: pctX(STAMP_LEFT), top: pctY(STAMP_TOP), width: pctW(STAMP_SIZE), height: pctH(STAMP_SIZE), transform: 'rotate(15deg)', transformOrigin: 'center' }} />
            </>
          )
        })()}

        <div className="absolute" style={{ right: pctX(16), top: pctY(16), fontSize: `${(11 / CANVAS_H) * 100}cqh`, color: 'rgba(255,255,255,0.9)', textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
          {s.certLabel}: JD2026000042
        </div>

        <div className="absolute left-0 right-0 bottom-0 text-center" style={{ background: 'rgba(5, 62, 0, 0.3)', color: '#fff', padding: `${(6 / CANVAS_H) * 100}cqh ${(12 / CANVAS_W) * 100}cqw`, fontSize: `${(11 / CANVAS_H) * 100}cqh`, lineHeight: 1.3 }}>
          {s.co2}
        </div>

        {bottomSlots.map(({ x, y, index }) => {
          const url = config.logosBottom[index]
          const filled = !!url
          return (
            <div
              key={`bottom-${index}`}
              className="absolute flex items-center justify-center rounded"
              style={{
                left: pctX(x),
                top: pctY(y),
                width: pctW(BOTTOM_SLOT_W),
                height: pctH(BOTTOM_SLOT_H),
                opacity: filled ? 1 : 0.6,
                border: url ? '1px solid rgba(255,255,255,0.7)' : '1.5px dashed rgba(255,255,255,0.9)',
                background: 'rgba(0,0,0,0.35)',
                borderRadius: '8px'
              }}
            >
              {url && <img src={absoluteUrl(url)} alt="" className="max-h-full max-w-full object-contain" draggable={false} />}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Универсальный PDF-модал: fetch PDF по URL → blob → iframe.
 */
const PdfModal: React.FC<{ title: string; pdfUrl: string; aspectRatio: number; onClose: () => void }> = ({
  title,
  pdfUrl,
  aspectRatio,
  onClose
}) => {
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string>('')
  const [err, setErr] = useState<string | null>(null)
  const authToken = localStorage.getItem('admin_token')

  useEffect(() => {
    let cancelled = false
    let localUrl: string | null = null
    setErr(null)
    setPdfBlobUrl('')
    fetch(pdfUrl, { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} })
      .then((r) => {
        if (!r.ok) throw new Error(`PDF ${r.status}`)
        return r.blob()
      })
      .then((blob) => {
        if (cancelled) return
        localUrl = URL.createObjectURL(blob)
        setPdfBlobUrl(localUrl)
      })
      .catch((e) => {
        if (!cancelled) setErr(String(e))
      })
    return () => {
      cancelled = true
      if (localUrl) URL.revokeObjectURL(localUrl)
    }
  }, [pdfUrl, authToken])

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content cert-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body cert-preview-body">
          <div
            className="cert-preview-frame"
            style={{ aspectRatio: String(aspectRatio), ['--ar' as string]: String(aspectRatio) }}
          >
            {err && <div className="cert-preview-error">Ошибка: {err}</div>}
            {!err && !pdfBlobUrl && <div className="cert-preview-loading">Генерация PDF…</div>}
            {!err && pdfBlobUrl && (
              <iframe
                title={title}
                src={`${pdfBlobUrl}#toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
                className="cert-preview-iframe"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const PreviewModal: React.FC<{ language: Language; token: number; onClose: () => void }> = ({
  language,
  token,
  onClose
}) => {
  const pdfUrl = `${API_URL}/api/admin/certificate-preview/pdf?language=${language}&t=${token}`
  return <PdfModal title="Предпросмотр сертификата" pdfUrl={pdfUrl} aspectRatio={CANVAS_W / CANVAS_H} onClose={onClose} />
}

const ThankYouModal: React.FC<{ language: Language; token: number; onClose: () => void }> = ({
  language,
  token,
  onClose
}) => {
  const pdfUrl = `${API_URL}/api/admin/certificate-preview/thank-you?language=${language}&t=${token}`
  // Portrait A4
  return <PdfModal title="Предпросмотр благодарственного письма" pdfUrl={pdfUrl} aspectRatio={595 / 842} onClose={onClose} />
}

const HandoutsModal: React.FC<{ language: Language; token: number; onClose: () => void }> = ({
  language,
  token,
  onClose
}) => {
  const pdfUrl = `${API_URL}/api/admin/certificate-preview/handouts?language=${language}&t=${token}`
  // Portrait A4 (может быть несколько страниц — PDF viewer сам скроллит)
  return <PdfModal title="Предпросмотр раздаточных материалов" pdfUrl={pdfUrl} aspectRatio={595 / 842} onClose={onClose} />
}

const LogoSlot: React.FC<{
  position: Position
  index: number
  url: string | undefined
  uploading: boolean
  onUpload: (file: File) => void
  onDelete: () => void
  onDragStart: () => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: () => void
  style?: React.CSSProperties
}> = ({ url, uploading, onUpload, onDelete, onDragStart, onDragOver, onDrop, style }) => {
  const inputRef = useRef<HTMLInputElement | null>(null)

  const handleClick = () => {
    if (!url && !uploading) inputRef.current?.click()
  }

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) onUpload(f)
    e.target.value = ''
  }

  return (
    <div
      className={
        'group absolute flex items-center justify-center rounded ' +
        (url ? 'cursor-move' : 'cursor-pointer')
      }
      style={{
        ...style,
        border: url ? '1px solid rgba(255,255,255,0.7)' : '1.5px dashed rgba(255,255,255,0.9)',
        background: 'rgba(0,0,0,0.35)',
        borderRadius: '8px'
      }}
      draggable={!!url}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={handleClick}
      title={url ? 'Перетащите для смены порядка' : 'Кликните чтобы загрузить'}
    >
      {url ? (
        <>
          <img
            src={absoluteUrl(url)}
            alt=""
            className="max-h-full max-w-full object-contain"
            draggable={false}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] text-white shadow group-hover:flex"
            aria-label="Удалить"
          >
            ×
          </button>
        </>
      ) : (
        <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.85)' }}>
          {uploading ? '…' : '+'}
        </span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/svg+xml,image/webp,image/jpeg"
        hidden
        onChange={handleFile}
      />
    </div>
  )
}
